import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ShieldCheck, ScrollText, Network, AlertOctagon,
  Play, ClipboardCheck, Users, Globe2, LogOut, Terminal, Laptop,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

const nav = [
  { to: "/app", key: "dashboard", icon: LayoutDashboard, roles: "*" },
  { to: "/app/catalog", key: "catalog", icon: ShieldCheck, roles: "*" },
  { to: "/app/policies", key: "policies", icon: ScrollText, roles: ["admin", "auditeur"] },
  { to: "/app/segments", key: "segments", icon: Network, roles: ["admin", "auditeur"] },
  { to: "/app/siem", key: "siem", icon: AlertOctagon, roles: ["admin", "auditeur"] },
  { to: "/app/scenarios", key: "scenarios", icon: Play, roles: "*" },
  { to: "/app/compliance", key: "compliance", icon: ClipboardCheck, roles: ["admin", "auditeur"] },
  { to: "/app/users", key: "users", icon: Users, roles: ["admin"] },
  { to: "/app/devices", key: "devices", icon: Laptop, roles: "*" },
  { to: "/app/sites", key: "sites", icon: Globe2, roles: ["admin", "auditeur"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isSim = user?.mode === "simulated";
  
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const items = nav.filter((n) => {
    // Scenarios only make sense in Simulated mode
    if (n.key === "scenarios" && user?.mode !== "simulated") return false;
    return n.roles === "*" || n.roles.includes(user?.role);
  });

  return (
    <div
      className={cn(
        "min-h-screen bg-[#050a0e] text-white font-sans cyber-grid-bg",
        isSim && "ring-2 ring-inset ring-orange-500/40"
      )}
      data-testid="app-layout"
    >
      {/* Top bar */}
      <header
        className={cn(
          "relative flex items-center justify-between border-b border-cyan-500/30 px-6 py-3 cyber-glass scan-line shadow-[0_4px_15px_-3px_rgba(0,229,255,0.1)]",
          isSim && "border-orange-500/50 shadow-[0_4px_15px_-3px_rgba(255,153,0,0.1)]"
        )}
      >
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Terminal className={cn("h-5 w-5", isSim ? "text-orange-400" : "text-cyan-400")} />
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-white/60">
                {t("app_name")}
              </div>
              <div className="text-sm font-semibold tracking-tight cyber-glow-text">{t("app_sub")}</div>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center gap-2 border-l border-white/10 pl-6">
            <div className="w-2 h-2 rounded-full bg-green-500 live-dot"></div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-green-400 font-bold">
              SYSTÈME SÉCURISÉ
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:block font-mono text-xs text-white/60 tracking-wider">
            {time.toLocaleTimeString('en-US', { hour12: false })}
          </div>

          <div
            data-testid="mode-indicator"
            className={cn(
              "px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.2em] rounded-sm",
              isSim
                ? "border-orange-500/50 text-orange-400 bg-orange-500/10 neon-flicker"
                : "border-cyan-500/50 text-cyan-400 bg-cyan-500/5"
            )}
          >
            {isSim ? t("mode_simulated") : t("mode_real")}
          </div>

          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors border border-white/10 px-2 py-1.5 rounded-sm"
          >
            {lang === "fr" ? "FR / EN" : "EN / FR"}
          </button>

          <div className="hidden md:flex flex-col items-end border-l border-white/10 pl-4">
            <div className="text-sm">{user?.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
              {user?.role} · {user?.site}
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="text-white/70 hover:text-red-400 hover:bg-red-500/10 ml-2"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t("logout")}</span>
          </Button>
        </div>
      </header>

      <div className="flex h-[calc(100vh-57px)]">
        <aside className="relative w-56 shrink-0 border-r border-cyan-900/30 hidden md:flex flex-col overflow-hidden bg-[#060d14]">
          {/* Subtle animated data stream in background */}
          <div className="absolute inset-0 data-stream-bg opacity-30 pointer-events-none"></div>
          
          <nav className="relative z-10 flex flex-col gap-1 px-3 py-6 flex-1 overflow-y-auto">
            {items.map((n, index) => {
              const Icon = n.icon;
              return (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <NavLink
                    to={n.to}
                    end={n.to === "/app"}
                    data-testid={`nav-${n.key}`}
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all duration-300",
                        isActive
                          ? "bg-cyan-500/15 text-cyan-300 border-l-2 border-cyan-400 pl-[10px] shadow-[inset_0_0_12px_rgba(0,229,255,0.06)]"
                          : "text-white/80 hover:bg-white/5 hover:text-cyan-300"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon 
                          className={cn(
                            "h-4 w-4 transition-transform group-hover:scale-110",
                            isActive && "drop-shadow-[0_0_5px_rgba(0,229,255,0.8)]"
                          )} 
                        />
                        <span>{t(n.key)}</span>
                      </>
                    )}
                  </NavLink>
                </motion.div>
              );
            })}
          </nav>

          <div className="relative z-10 p-4 border-t border-white/5 bg-black/20">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 live-dot"></div>
              <span className="text-[10px] font-mono tracking-widest text-green-500 font-semibold">
                THREAT LEVEL: LOW
              </span>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 overflow-y-auto relative z-10" data-testid="app-main">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
